import Service from 'react-reservice';

import technicalIndicatorApi, { ITechnicalIndicator } from '@webtrader/core/api/user/indicator';
import { EChartIndicator, chartMainAndSubIndicators } from '@webtrader/core/chart/indicators';
import { IApiError } from '@webtrader/core/request/api';
import { mapResponseCodeToMessage } from '@webtrader/core/request/message';

interface ITechnicalIndicatorContext {
  loading: boolean;
  updating: boolean;
  technicalIndicators: ITechnicalIndicator[];
}

const defaultContext: ITechnicalIndicatorContext = {
  loading: false,
  updating: false,
  technicalIndicators: []
};

export interface IUpdateIndicatorParams {
  technicalIndicatorList: ITechnicalIndicator[];
  onComplete?: (success: boolean, message?: string) => void;
}

class TechnicalIndicatorService extends Service<ITechnicalIndicatorContext> {
  private updateTechnicalIndicatorAbortController = new AbortController();

  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public getAllTechnicalIndicators = async () => {
    try {
      const { result } = await technicalIndicatorApi.get();

      if (result && result.length > 0) {
        result.sort((a, b) => chartMainAndSubIndicators.indexOf(a.type) - chartMainAndSubIndicators.indexOf(b.type));
      }
      this.context.technicalIndicators = result || [];

      return this.context.technicalIndicators;
    } catch {
      return [];
    }
  };

  public updateTechnicalIndicator = async (params: IUpdateIndicatorParams) => {
    const { technicalIndicatorList, onComplete } = params;

    try {
      this.context.updating = true;
      this.updateTechnicalIndicatorAbortController.abort();
      this.updateTechnicalIndicatorAbortController = new AbortController();

      await technicalIndicatorApi.update(
        { technicalIndicatorList },
        this.updateTechnicalIndicatorAbortController.signal
      );
      onComplete?.(true);
    } catch (error) {
      const { code, message } = error as IApiError;
      onComplete?.(false, mapResponseCodeToMessage(code, message));
    } finally {
      this.context.updating = false;
    }
  };

  public clearAllTechnicalIndicators = async (params: IUpdateIndicatorParams) => {
    await this.updateTechnicalIndicator(params);
    this.getAllTechnicalIndicators();
  };

  // Service functions
  public setTechnicalIndicators = async (
    indicator: EChartIndicator,
    onError?: () => void,
    onSuccess?: (isIndicatorEnabled: boolean) => void
  ) => {
    const { technicalIndicators } = this.context;

    const tempTechnicalIndicators = [...technicalIndicators];

    const selectedIndicator = tempTechnicalIndicators.find((i) => i.type === indicator);
    const isIndicatorEnabled = !selectedIndicator?.enabled;

    await this.updateTechnicalIndicator({
      technicalIndicatorList: [{ type: indicator, enabled: isIndicatorEnabled }],
      onComplete: (success) => {
        if (!success) {
          onError?.();
          return;
        }

        if (selectedIndicator === undefined) {
          tempTechnicalIndicators.push({ type: indicator, enabled: true });
        } else if (tempTechnicalIndicators.length > 0) {
          const index = tempTechnicalIndicators.findIndex((i) => i.type === indicator);
          tempTechnicalIndicators[index].enabled = isIndicatorEnabled;
        }

        this.context.technicalIndicators = tempTechnicalIndicators;
        onSuccess?.(isIndicatorEnabled);
      }
    });
  };
}

export default new TechnicalIndicatorService();
